import React, { useEffect, useState } from "react";
import Select from "react-select";
import { toast, Toaster } from "react-hot-toast";
import { ArrowLeft, Download, Search, RefreshCw, Eye, X } from "lucide-react";
import * as XLSX from "xlsx";
import { getBillSearchByNumbers } from "../../services/masterDataService";
import { selectStyles } from "../../helpers";
import "./BillSearchByNumbersReportdetails.css";

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const formatDate = (v) => (v ? new Date(v).toLocaleDateString("en-US") : "");
const formatMoney = (v) =>
  v == null || v === "" ? "" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const joinAddress = (address, city, state, zip) =>
  [address, city, state, zip]
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(", ");

const NoteCell = ({ text, onOpen }) => {
  const value = text == null ? "" : String(text);
  if (!value) return <td />;
  const isLong = value.length > 40;
  return (
    <td>
      <div className="bsbd-note-cell">
        <span className="bsbd-note-clamp">{isLong ? value.slice(0, 40) + "..." : value}</span>
        {isLong && (
          <button type="button" className="bsbd-eye" onClick={() => onOpen({ title: "Note", text: value })}>
            <Eye size={14} />
          </button>
        )}
      </div>
    </td>
  );
};

const BillSearchByNumbersReportdetails = ({ domain, companyId, companyName, billNumbers, fromDate, toDate, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [popup, setPopup] = useState(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(pageNumber, totalPages);

  const loadPage = async (page, size, search = appliedSearch) => {
    if (!domain || !companyId) {
      toast.error("Missing company. Go back and search again.");
      return;
    }
    setLoading(true);
    try {
      const res = await getBillSearchByNumbers({
        domain,
        billNumbers: billNumbers || "",
        companyIds: companyId,
        fromDate: fromDate || null,
        toDate: toDate || null,
        tabName: "Details",
        pageNumber: page,
        pageSize: size,
        isExport: false,
      });
      const data = res?.details ? res : res?.data || {};
      setRows(data?.details || []);
      setTotalCount(data?.totalCount || 0);
      setPageNumber(page);
      setPageSize(size);
      setAppliedSearch(search || "");
      if (!(data?.details || []).length) toast("No data");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(1, pageSize, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, companyId]);

  const handleSearch = () => loadPage(1, pageSize, searchText.trim());

  const mapRow = (r) => ({
    Attorney: r.attorney,
    "Bill Number": r.szBillNumber,
    "Case No": r.szCaseNo,
    "Bill Date": formatDate(r.dtBillDate),
    "Billed Amount": r.fltBillAmount,
    "Paid Amount": r.paidAmount,
    "WriteOff Amount": r.fltWriteOff,
    "Paid Flag": r.bitPaid,
    "Amount Applied": r.fltAmountApplied,
    Balance: r.fltBalance,
    Interest: r.fltInterest,
    Type: r.szType,
    "Test Type": r.szTestType,
    "Write Off Flag": r.bitWriteOffFlag,
    "Payment State": r.iPaymentState,
    Status: r.szBillStatusName,
    "First Visit": formatDate(r.dtFirstVisitDate),
    "Proc Date": r.procDate,
    Specialty: r.speciality,
    "Procedure Group": r.szProcedureGroup,
    "Status Code": r.szBillStatusCode,
    Transfer: r.btTransfer ? "Yes" : "No",
    Denial: r.btDenial,
    Verification: r.btVerification,
    EOR: r.btEor,
    Provider: r.szProviderName,
    "Case Type": r.mstCaseType,
    "Case Status": r.caseStatus,
    Notes: r.szBillNotes,
    Company: r.companyName,
    "First Name": r.szPatientFirstName,
    "Last Name": r.szPatientLastName,
    Patient: r.szPatientName,
    "Claim #": r.szClaimNumber,
    "Policy #": r.szPolicyNo,
    "Has Payment": r.hasPayment,
    Collection: r.collectionName,
    "Collection Id": r.szBillCollectionId,
    Insurance: r.insuranceName,
    "Denial Reasons": r.denialReasons,
    VerificationText: r.verification,
    DOB: formatDate(r.dob),
    SSN: r.ssn,
    Cell: r.patientCellPhone,
    "Home Phone": r.patientHomePhone,
    DOA: formatDate(r.doa),
    Address: joinAddress(r.address, r.city, r.state, r.zip),
    "PI Attorney": r.piAttorney,
    "Attorney Phone": r.attorneyPhone,
    Funder: r.funderName,
    Portfolio: r.portfolioName,
    Batch: r.batchNumber,
    Funded: r.isFunded,
    "Funder Note": r.funderNote,
  });

  const handleDownload = async () => {
    setLoading(true);
    try {
      const res = await getBillSearchByNumbers({
        domain,
        billNumbers: billNumbers || "",
        companyIds: companyId,
        fromDate: fromDate || null,
        toDate: toDate || null,
        tabName: "Details",
        isExport: true,
      });
      const data = res?.details ? res : res?.data || {};
      const list = data?.details || [];
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      const mapped = list.map(mapRow);
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Details");
      XLSX.writeFile(wb, `BillSearch_${companyId}_Details.xlsx`);
      toast.success(`Exported ${list.length} rows`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bsbd-page">
      <Toaster position="top-right" />

      <div className="bsbd-header">
        <button type="button" className="bsbd-btn bsbd-btn-back" onClick={onBack}>
          <ArrowLeft size={12} />
          Back
        </button>

        <div className="bsbd-title">
          <h3>
            {companyName || "Company"} ({companyId})
          </h3>
          <span>Bills matching the search</span>
        </div>

        <div className="bsbd-search-row">
          <input
            className="bsbd-input"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Bill # / Case # / Patient"
          />
          <button type="button" className="bsbd-btn bsbd-btn-back" onClick={handleSearch} disabled={loading}>
            <Search size={14} />
            Search
          </button>
          <button
            type="button"
            className="bsbd-btn bsbd-btn-reset"
            onClick={() => {
              setSearchText("");
              loadPage(1, pageSize, "");
            }}
            disabled={loading}
          >
            <RefreshCw size={14} />
            Reset
          </button>
        </div>

        <button type="button" className="bsbd-btn bsbd-btn-reset" onClick={handleDownload} disabled={loading || !rows.length}>
          <Download size={14} />
          Excel
        </button>
      </div>

      <div className="bsbd-table-wrap">
        <table className="bsbd-table">
          <thead>
            <tr>
              <th>Attorney</th>
              <th>Bill #</th>
              <th>Case #</th>
              <th>Bill Date</th>
              <th className="bsbd-num">Billed</th>
              <th className="bsbd-num">Paid</th>
              <th className="bsbd-num">Write Off</th>
              <th>Paid Flag</th>
              <th className="bsbd-num">Amount Applied</th>
              <th className="bsbd-num">Balance</th>
              <th className="bsbd-num">Interest</th>
              <th>Type</th>
              <th>Test Type</th>
              <th>Write Off Flag</th>
              <th>Payment State</th>
              <th>Status</th>
              <th>First Visit</th>
              <th>Proc Date</th>
              <th>Specialty</th>
              <th>Procedure Group</th>
              <th>Status Code</th>
              <th>Transfer</th>
              <th>Denial</th>
              <th>Verification</th>
              <th>EOR</th>
              <th>Provider</th>
              <th>Case Type</th>
              <th>Case Status</th>
              <th>Notes</th>
              <th>Company</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Patient</th>
              <th>Claim #</th>
              <th>Policy #</th>
              <th>Has Payment</th>
              <th>Collection</th>
              <th>Collection Id</th>
              <th>Insurance</th>
              <th>Denial Reasons</th>
              <th>Verification</th>
              <th>DOB</th>
              <th>SSN</th>
              <th>Cell</th>
              <th>Home Phone</th>
              <th>DOA</th>
              <th className="bsbd-address">Address</th>
              <th>PI Attorney</th>
              <th>Attorney Phone</th>
              <th>Funder</th>
              <th>Portfolio</th>
              <th>Batch</th>
              <th>Funded</th>
              <th>Funder Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={53} className="bsbd-empty">
                  <div className="bsbd-empty-inner">{loading ? "Loading..." : "No data"}</div>
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.szBillNumber}>
                  <td>{r.attorney}</td>
                  <td>{r.szBillNumber}</td>
                  <td>{r.szCaseNo}</td>
                  <td>{formatDate(r.dtBillDate)}</td>
                  <td className="bsbd-num">{formatMoney(r.fltBillAmount)}</td>
                  <td className="bsbd-num">{formatMoney(r.paidAmount)}</td>
                  <td className="bsbd-num">{formatMoney(r.fltWriteOff)}</td>
                  <td>{r.bitPaid}</td>
                  <td className="bsbd-num">{formatMoney(r.fltAmountApplied)}</td>
                  <td className="bsbd-num">{formatMoney(r.fltBalance)}</td>
                  <td className="bsbd-num">{formatMoney(r.fltInterest)}</td>
                  <td>{r.szType}</td>
                  <td>{r.szTestType}</td>
                  <td>{r.bitWriteOffFlag}</td>
                  <td>{r.iPaymentState}</td>
                  <td>{r.szBillStatusName}</td>
                  <td>{formatDate(r.dtFirstVisitDate)}</td>
                  <td>{r.procDate}</td>
                  <td>{r.speciality}</td>
                  <td>{r.szProcedureGroup}</td>
                  <td>{r.szBillStatusCode}</td>
                  <td>{r.btTransfer ? "Yes" : "No"}</td>
                  <td>{r.btDenial}</td>
                  <td>{r.btVerification}</td>
                  <td>{r.btEor}</td>
                  <td>{r.szProviderName}</td>
                  <td>{r.mstCaseType}</td>
                  <td>{r.caseStatus}</td>
                  <NoteCell text={r.szBillNotes} onOpen={setPopup} />
                  <td>{r.companyName}</td>
                  <td>{r.szPatientFirstName}</td>
                  <td>{r.szPatientLastName}</td>
                  <td>{r.szPatientName}</td>
                  <td>{r.szClaimNumber}</td>
                  <td>{r.szPolicyNo}</td>
                  <td>{r.hasPayment}</td>
                  <td>{r.collectionName}</td>
                  <td>{r.szBillCollectionId}</td>
                  <td>{r.insuranceName}</td>
                  <td className="bsbd-wrap">{r.denialReasons}</td>
                  <td className="bsbd-wrap">{r.verification}</td>
                  <td>{formatDate(r.dob)}</td>
                  <td>{r.ssn}</td>
                  <td>{r.patientCellPhone}</td>
                  <td>{r.patientHomePhone}</td>
                  <td>{formatDate(r.doa)}</td>
                  <td className="bsbd-address">{joinAddress(r.address, r.city, r.state, r.zip)}</td>
                  <td>{r.piAttorney}</td>
                  <td>{r.attorneyPhone}</td>
                  <td>{r.funderName}</td>
                  <td>{r.portfolioName}</td>
                  <td>{r.batchNumber}</td>
                  <td>{r.isFunded}</td>
                  <NoteCell text={r.funderNote} onOpen={setPopup} />
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalCount > 0 && (
        <div className="bsbd-pager">
          <Select
            options={pageSizeOptions}
            value={pageSizeOptions.find((o) => o.value === pageSize)}
            onChange={(opt) => loadPage(1, opt.value, appliedSearch)}
            styles={selectStyles}
            menuPlacement="top"
            isSearchable={false}
          />
          <span>
            {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, totalCount)} of {totalCount}
          </span>
          <button type="button" className="bsbd-btn bsbd-btn-reset" disabled={safePage <= 1 || loading} onClick={() => loadPage(safePage - 1, pageSize, appliedSearch)}>
            Prev
          </button>
          <button type="button" className="bsbd-btn bsbd-btn-reset" disabled={safePage >= totalPages || loading} onClick={() => loadPage(safePage + 1, pageSize, appliedSearch)}>
            Next
          </button>
        </div>
      )}

      {popup && (
        <div className="bsbd-overlay" onClick={() => setPopup(null)}>
          <div className="bsbd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bsbd-modal-header">
              <h3>{popup.title}</h3>
              <button type="button" className="bsbd-close" onClick={() => setPopup(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="bsbd-modal-body">
              <pre>{popup.text}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillSearchByNumbersReportdetails;